import "./Locks.css";

import { Button, Callout, MessageState } from "../../../ds";

import { Navigate } from "react-router-dom";

export const IdentityUnavailableText = "Identity is unavailable, try again in a few minutes.";

/**
 * Ark.Core answered 503 identity-unavailable: it cannot tell what the caller may read, so it shows nothing rather than guess.
 * Full: the library empty state (`MessageState`) in place of the screen (Spaces, a page, the tree). Compact: a warning callout inside a section.
 */
export function IdentityUnavailableState(props: { onRetry?: () => void; compact?: boolean }) {
    if (props.compact) {
        return (
            <div className="ark-identity-unavailable is-compact" role="alert">
                <Callout tone="warning">
                    <p>{IdentityUnavailableText}</p>
                    {props.onRetry ? <Button type="link" size="sm" text="Try again" onClick={props.onRetry} /> : null}
                </Callout>
            </div>
        );
    }
    return (
        <div className="ark-identity-unavailable" role="alert">
            <MessageState
                title="Identity is unavailable"
                text={IdentityUnavailableText}
                secondaryLabel={props.onRetry ? "Try again" : undefined}
                onSecondary={props.onRetry}
            />
        </div>
    );
}

/** The manifest is `partial`: lock grants were unknown, so locked subtrees are missing from the listing. */
export function PartialLocksBanner(props: { partial: boolean }) {
    if (!props.partial) return null;
    return (
        <div className="ark-partial-locks" role="status">
            <Callout tone="warning">Some locked pages are not shown while Identity is unavailable</Callout>
        </div>
    );
}

export type LoadError = "not-found" | "identity-unavailable" | undefined;

/**
 * What a failed page or tree load shows: a hidden page and a page that does not exist are the same 404, and both go to the app's
 * not-found view (Ark never says a page is forbidden); Identity being unavailable gets its own retry state.
 */
export function LoadGate(props: { error: LoadError; onRetry?: () => void; children: React.ReactNode }) {
    if (props.error === "not-found") return <Navigate to="/notfound" replace />;
    if (props.error === "identity-unavailable") return <IdentityUnavailableState onRetry={props.onRetry} />;
    return <>{props.children}</>;
}
